import { useAuth0 } from "@auth0/auth0-react";
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { PageLayout } from "../components/PageLayout";
import { PageLoader } from "../components/PageLoader";
import { ROUTES } from "../config/routes";
import UserService from "../services/UserService";

export const CallbackPage = () => {
  const { error, isAuthenticated, user, loginWithRedirect, isLoading, getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();

  useEffect(() => {
    // If still loading, just show loader
    if (isLoading) {
      return;
    }

    // If not authenticated, send to Auth0 login
    if (!isAuthenticated && !error) {
      loginWithRedirect({
        appState: { returnTo: ROUTES.CALLBACK },
      });
      return;
    }

    // If authenticated:
    // 1) Ensure auth0_users document exists with defaults via /verify
    // 2) Check subscription; if active, go to dashboard, otherwise go to pricing
    if (isAuthenticated && user) {
      (async () => {
        try {
          const token = await getAccessTokenSilently();
          // Fire-and-forget verify to create/update auth0_users with reference defaults
          await UserService.verify(user, token);

          // Check subscription status with a fresh Stripe lookup (forceRefresh=true)
          // CRITICAL: Only use Stripe (/api/payments/subscriptions), never MongoDB subscription field
          const sub = await UserService.getUserSubscription(token, user, true);
          console.log("CallbackPage: Subscription check result from Stripe:", sub);

          // Determine active subscription using the same logic as SubscriptionsComponent
          // STRIPE IS THE SOURCE OF TRUTH - ignore any MongoDB subscription field
          let hasActiveSub = false;
          if (sub) {
            // Primary check: main subscription object from Stripe
            if (
              sub.status === true &&
              sub.subscriptionType &&
              sub.subscriptionType !== "Default" &&
              sub.subscriptionId // Must have a real subscription ID from Stripe
            ) {
              hasActiveSub = true;
              console.log("CallbackPage: ✅ Active subscription found in main object:", sub.subscriptionType, "ID:", sub.subscriptionId);
            } else if (Array.isArray(sub.allSubSctiptions) && sub.allSubSctiptions.length > 0) {
              // Fallback: look into allSubSctiptions for any active, non-default plan
              const activeSub = sub.allSubSctiptions.find(
                (s) =>
                  s &&
                  s.status === true &&
                  s.subscriptionType &&
                  s.subscriptionType !== "Default" &&
                  s.subscriptionId && // Must have a real subscription ID
                  (s.subscriptionType === "CoPilot" ||
                    s.subscriptionType === "Silver" ||
                    s.subscriptionType === "Gold" ||
                    s.subscriptionType === "Platinum")
              );
              if (activeSub) {
                hasActiveSub = true;
                console.log("CallbackPage: ✅ Active subscription found in allSubSctiptions:", activeSub.subscriptionType, "ID:", activeSub.subscriptionId);
              }
            }
          }

          console.log("CallbackPage: hasActiveSub =", hasActiveSub);

          if (hasActiveSub) {
            console.log("CallbackPage: ✅ Redirecting to Dashboard (user has active subscription)");
            // Use replace: true to prevent back button from going to callback
            navigate(ROUTES.DASHBOARD, { replace: true });
            return;
          } else {
            console.log("CallbackPage: ⚠️ No active subscription found, redirecting to Pricing");
            navigate(ROUTES.PRICING, { replace: true });
            return;
          }
        } catch (e) {
          console.error("CallbackPage: Error during verify/subscription check:", e);
          // On error, default to pricing
          navigate(ROUTES.PRICING, { replace: true });
        }
      })();
      return;
    }
  }, [isAuthenticated, isLoading, error, user, loginWithRedirect, navigate, getAccessTokenSilently]);

  // Show loading while checking subscription
  return (
    <PageLayout>
      <PageLoader />
    </PageLayout>
  );
};
